<script setup>
import { computed, reactive, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisField from 'dashboard/components-next/LaisField.vue';
import LaisModal from 'dashboard/components-next/LaisModal.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';

const props = defineProps({
  note: { type: Object, default: null },
  saving: { type: Boolean, default: false },
  show: { type: Boolean, default: false },
});

const emit = defineEmits(['close', 'submit']);
const { t } = useI18n();
const form = reactive({ title: '', description: '', important: false });
const errors = reactive({ title: '', description: '' });

const isEditing = computed(() => Boolean(props.note));
const title = computed(() =>
  isEditing.value
    ? t('POST_SALE.NOTES.FORM.EDIT_TITLE')
    : t('POST_SALE.NOTES.FORM.CREATE_TITLE')
);
const submitLabel = computed(() =>
  isEditing.value
    ? t('POST_SALE.NOTES.ACTIONS.SAVE_EDIT')
    : t('POST_SALE.NOTES.ACTIONS.ADD')
);

const reset = () => {
  form.title = props.note?.title || '';
  form.description = props.note?.description || '';
  form.important = Boolean(props.note?.important);
  errors.title = '';
  errors.description = '';
};

watch(() => [props.note, props.show], reset, { immediate: true });

const submit = () => {
  errors.title = form.title.trim() ? '' : t('POST_SALE.NOTES.FORM.REQUIRED');
  errors.description = form.description.trim()
    ? ''
    : t('POST_SALE.NOTES.FORM.REQUIRED');
  if (errors.title || errors.description) return;

  emit('submit', {
    title: form.title.trim(),
    description: form.description.trim(),
    important: form.important,
    expected_version: props.note?.version,
  });
};
</script>

<template>
  <LaisModal
    :show="show"
    :title="title"
    max-width-class="max-w-[620px]"
    @close="emit('close')"
  >
    <div class="grid gap-4">
      <LaisField
        :label="t('POST_SALE.NOTES.FORM.TITLE')"
        :error="errors.title"
        required
      >
        <input
          v-model="form.title"
          class="min-h-10 rounded-lais border border-lais-border bg-white px-3 text-sm"
          :placeholder="t('POST_SALE.NOTES.FORM.TITLE_PLACEHOLDER')"
        />
      </LaisField>
      <LaisField
        :label="t('POST_SALE.NOTES.FORM.DESCRIPTION')"
        :error="errors.description"
        required
      >
        <textarea
          v-model="form.description"
          rows="8"
          class="rounded-lais border border-lais-border bg-white px-3 py-2 text-sm"
          :placeholder="t('POST_SALE.NOTES.FORM.DESCRIPTION_PLACEHOLDER')"
        />
      </LaisField>
      <label class="flex items-center gap-2 text-sm text-lais-text-body">
        <input v-model="form.important" type="checkbox" />
        <span>{{ t('POST_SALE.NOTES.FORM.IMPORTANT') }}</span>
      </label>
    </div>
    <template #footer>
      <div class="flex justify-end">
        <LaisPrimaryButton :is-loading="saving" @click="submit">
          {{ submitLabel }}
        </LaisPrimaryButton>
      </div>
    </template>
  </LaisModal>
</template>
